import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { TranslationUpdateModal, TranslationCreateModal } from '../../components';

import {
  Grid,
  Button,
  Icon,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Tooltip,
  IconButton,
  Paper,
  Chip
} from '@material-ui/core';
import { LoaderSize } from '../../constants/constants';
import { useStyles } from './styles';
import SettingsService from '../../hooks/SettingsService';
import { TranslationViewModel } from '../../models/settings';
import { useTranslation } from 'react-i18next';
import i18n from '../../hooks/i18n';
import UserRole from '../../constants/user-role';

const LanguageSettings: React.FC<any> = () => {
  const classes = useStyles();
  const settingsService = new SettingsService();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [translations, setTranslations] = React.useState([] as TranslationViewModel[]);
  const [total, setTotal] = React.useState(0);
  const [selectedTranslation, setSelectedTranslation] = React.useState({} as TranslationViewModel);
  const [updateTranslationModalOpened, setUpdateTranslationModalOpened] = React.useState(false);
  const [createTranslationModalOpened, setCreateTranslationModalOpened] = React.useState(false);

  const loadTranslations = async () => {
    setIsBusy(true);

    settingsService
      .getTranslations()
      .then((result) => {
        setTranslations(result);
        setTotal(result.length);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const load = async () => loadTranslations();
      load();
    }
  }, [loaded]);

  const renderActionCell = (trans: TranslationViewModel) => {
    return state.role === UserRole.admin ? (
      <Tooltip arrow title={t('f-trans-upd-tool')}>
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedTranslation(trans);
            setUpdateTranslationModalOpened(true);
          }}
        >
          <Icon>{'translate'}</Icon>
        </IconButton>
      </Tooltip>
    ) : null;
  };

  const handleUpdateTranslationModalClose = (result: boolean) => {
    setUpdateTranslationModalOpened(false);

    if (result) {
      setLoaded(false);
      i18n.reloadResources();
    }
  };

  const handleCreateTranslationModalClose = (result: boolean) => {
    setCreateTranslationModalOpened(false);

    if (result) {
      setLoaded(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item container md={6} spacing={1}>
        <Grid item>
          <Icon fontSize="large" color="primary">
            {'translate'}
          </Icon>
        </Grid>
        <Grid item>
          <Title>{t('f-trans-title')}</Title>
        </Grid>
        <Grid item>
          <Chip label={total} variant="outlined" />
        </Grid>
      </Grid>

      <Grid item container md={6} className={classes.justifyRight}>
        <Grid item>
          <ReloadIconButton action={() => setLoaded(false)} />
        </Grid>

        <Grid item>
          <Button
            size="medium"
            disabled={state.role !== UserRole.admin}
            color="default"
            startIcon={<Icon>{'add_circle_outline'}</Icon>}
            onClick={() => setCreateTranslationModalOpened(true)}
            variant="contained"
          >
            {t('f-com-create-new')}
          </Button>
        </Grid>
      </Grid>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && (
        <Grid item container md={12}>
          <Grid item md={12}>
            <Paper elevation={1}>
              <Table size="small">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>{t('f-settings-lang')}</TableCell>
                    <TableCell>{t('f-trans-lang-iso')}</TableCell>
                    <TableCell>{t('f-com-updated')}</TableCell>
                    <TableCell>{''}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {translations.map((trans) => (
                    <TableRow key={trans.id} hover>
                      <TableCell>{trans.language}</TableCell>
                      <TableCell>{trans.isoCode}</TableCell>
                      <TableCell>{trans.updated ? new Date(trans.updated).toLocaleString() : '-'}</TableCell>
                      <TableCell>{renderActionCell(trans)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}

      <TranslationUpdateModal
        opened={updateTranslationModalOpened}
        onModalAction={handleUpdateTranslationModalClose}
        translation={selectedTranslation}
      />

      <TranslationCreateModal opened={createTranslationModalOpened} onModalAction={handleCreateTranslationModalClose} />
    </Grid>
  );
};

export default LanguageSettings;
